import type { UserNode } from '@revelio/data-access';

import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type TreeFilter,
} from '../../../filters/types/filters';
import { RICS_LEVEL_ID_TO_TAG } from './company';
import { buildTreeFilterNodes, wrapExprNode } from './node-builders';

/**
 * Build industry filter node from filter state.
 */
export const buildIndustryFilter = (filters: Filter[]): UserNode | null => {
  const industryFilter = findFilterById(filters, FilterId.INDUSTRY);
  if (!industryFilter || industryFilter.type !== FilterType.TREE) {
    return null;
  }

  const exprNode = buildTreeFilterNodes(industryFilter, RICS_LEVEL_ID_TO_TAG);
  if (!exprNode) return null;

  return wrapExprNode(exprNode, industryFilter.isNot);
};

/**
 * Build multi industry filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiIndustryFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiIndustry = findFilterById(filters, FilterId.MULTI_INDUSTRY);

  if (!multiIndustry || !isMultiFilter(multiIndustry)) {
    return null;
  }

  if (multiIndustry.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiIndustry.instances
    .filter((inst): inst is TreeFilter => inst?.type === FilterType.TREE)
    .map((instance) => {
      const exprNode = buildTreeFilterNodes(instance, RICS_LEVEL_ID_TO_TAG);
      if (!exprNode) return null;

      return wrapExprNode(exprNode, instance.isNot);
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};
